import React, {
    FunctionComponent,
    ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import './index.less';
import { FoodSalad as FoodMerchantsIcon } from 'Components/icons';
import { theme } from 'variant';
import LocationHeader from 'Components/location-header/location-header';
import { BreadcrumbSegment } from 'Components/routed-breadcrumb/routed-breadcrumb';
import { useTranslation } from 'react-i18next';
import { ActionMenu } from 'Components/action-menu';
import { ActionMenuOption, disableActionMenuItem } from 'Components/action-menu/action-menu';
import { useSearchDebounce, useService, useStores } from 'Hooks';
import { Layout, Table } from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import { TdWithImage } from 'Components/td-with-image';
import {
    ColumnType,
    Key,
    SorterResult,
    SortOrder,
    TablePaginationConfig,
} from 'antd/lib/table/interface';
import { TableFilters } from 'Components/table-filters';
import { autorun } from 'mobx';
import { FoodMerchantService } from 'Services/FoodMerchantService';
import FilterStore from 'Stores/FilterStore';
import { FoodMerchantDto } from 'Api/Features/FoodMerchants/Dtos/FoodMerchantDto';
import { MIN_ITEMS_FOR_REORDER, PAGE_SIZE, TABLE_COL_ORDER_DEFAULT_WIDTH } from 'Models/Constants';
import { FoodMerchant } from 'Models/FoodMerchant/FoodMerchant';
import { SortDirectionDto } from 'Api/Features/General/Dtos/SortDirectionDto';
import FoodMerchantModal from './food-merchant-modal';
import ReorderFoodMerchantsModal from './reorder-food-merchant-modal';
import { GetFoodMerchantsSortColumnDto } from 'Api/Features/FoodMerchants/Dtos/GetFoodMerchantsSortColumnDto';
import UserPermissionUtils from 'Utils/UserPermissionUtils';

const { Content } = Layout;

const initialPaginationState: TablePaginationConfig = {
    pageSize: PAGE_SIZE,
    defaultPageSize: PAGE_SIZE,
    showSizeChanger: true,
    position: ['bottomRight', 'topRight'],
};

const breadcrumbs: BreadcrumbSegment[] = [
    {
        path: 'management',
        nameKey: 'Location.management_title',
    },
    {
        path: 'merchants',
        nameKey: 'Food.food_and_beverages_merchant',
    },
];

const FoodMerchants: FunctionComponent = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string}>();
    const history = useHistory();
    const filterStoreRef = useRef(new FilterStore());
    const foodMerchantService = useService(FoodMerchantService);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<FoodMerchant[]>([]);
    const [pagination, setPagination] = useState<TablePaginationConfig>(initialPaginationState);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [reorderModalOpen, setReorderModalOpen] = useState<boolean>(false);
    const { userStore } = useStores();
    const [actions, setActions] = useState<ActionMenuOption[]>([
        {
            key: 'create',
            title: t('Food.merchant_create'),
            action: (): void => setCreateModalOpen(true),
        },
        {
            key: 'reorder',
            title: t('change_ordering'),
            action: (): void => setReorderModalOpen(true),
        },
    ]);

    const getTableColumnsSortHandler = () => {
        return (catA: FoodMerchant, catB: FoodMerchant): number => 0;
    };

    const getTableColumnsSortDirections = (): SortOrder[] => {
        return ['ascend', 'descend', 'ascend']; // # Force to always have ascend or descend states
    };

    const getTableColumnSortOrder = (
        currentSort: SorterResult<FoodMerchant>,
        columnKey: GetFoodMerchantsSortColumnDto
    ): SortOrder => {
        return columnKey === currentSort.columnKey ? (currentSort.order as SortOrder) : null;
    };

    const getTableColumns = (
        currentSort: SorterResult<FoodMerchant>
    ): ColumnType<FoodMerchant>[] => {
        return [
            {
                key: GetFoodMerchantsSortColumnDto.Order,
                title: t('Table.column_order'),
                dataIndex: 'order',
                render: (key: string): string => key || '0',
                sorter: getTableColumnsSortHandler(),
                sortDirections: getTableColumnsSortDirections(),
                sortOrder: getTableColumnSortOrder(
                    currentSort,
                    GetFoodMerchantsSortColumnDto.Order
                ),
                width: TABLE_COL_ORDER_DEFAULT_WIDTH,
            },
            {
                key: GetFoodMerchantsSortColumnDto.Name,
                title: t('name'),
                render: (foodMerchant: FoodMerchantDto): ReactNode | null => (
                    <TdWithImage
                        defaultImg={<FoodMerchantsIcon fill={theme['white']} />}
                        imgSrc={foodMerchant?.imageUrl}
                    >
                        {foodMerchant?.name}
                    </TdWithImage>
                ),
                sorter: getTableColumnsSortHandler(),
                sortDirections: getTableColumnsSortDirections(),
                sortOrder: getTableColumnSortOrder(currentSort, GetFoodMerchantsSortColumnDto.Name),
            },
            {
                key: 'type',
                title: t('Type'),
                render: (foodMerchant: FoodMerchantDto): string =>
                    foodMerchant.bbotRestaurant
                        ? t('Food.merchant_type_bbot')
                        : t('Food.merchant_type_mrp'),
                width: TABLE_COL_ORDER_DEFAULT_WIDTH,
            },
        ];
    };

    const defaultSortOrder: SorterResult<FoodMerchant> = {
        columnKey: GetFoodMerchantsSortColumnDto.Order as Key,
        order: 'ascend' as SortOrder,
    };

    const tableToDtoSortOrder = (
        tableSort: SorterResult<FoodMerchant>
    ): [GetFoodMerchantsSortColumnDto, SortDirectionDto] => {
        const sortColumn: GetFoodMerchantsSortColumnDto = tableSort.columnKey as GetFoodMerchantsSortColumnDto;
        const sortDirection: SortDirectionDto =
            tableSort.order === 'descend'
                ? SortDirectionDto.Descending
                : SortDirectionDto.Ascending;

        return [sortColumn, sortDirection];
    };

    const [defaultColumnSort, defaultDirectionSort] = tableToDtoSortOrder(defaultSortOrder);
    const defaultTableColumns = getTableColumns(defaultSortOrder);
    const [columns, setColumns] = useState<ColumnType<FoodMerchant>[]>(defaultTableColumns);

    const fetch = useCallback(
        async (params: {
            pagination: TablePaginationConfig;
            sortColumn: GetFoodMerchantsSortColumnDto;
            sortDirection: SortDirectionDto;
            searchTerm: string;
        }) => {
            setLoading(true);
            try {
                const [items, totalItems] = await foodMerchantService.getFoodMerchants({
                    pageSize: params.pagination.pageSize || PAGE_SIZE,
                    page: (params.pagination.current || 1) - 1,
                    sortColumn: params.sortColumn,
                    sortDirection: params.sortDirection,
                    locationIds: [id],
                    searchTerm: params.searchTerm,
                });

                setData(items);

                // disable reorder option
                setActions( value =>
                    disableActionMenuItem(value, 'reorder', items.length < MIN_ITEMS_FOR_REORDER)
                );

                setPagination({
                    ...params.pagination,
                    total: totalItems,
                });
            } finally {
                setLoading(false);
            }
        },
        [foodMerchantService, id]
    );

    const handleTableChange = (
        pagination: TablePaginationConfig,
        filters: Record<string, Key[] | null>,
        sorter: SorterResult<FoodMerchant> | SorterResult<FoodMerchant>[]
    ): void => {
        if (Array.isArray(sorter)) {
            // # We support single column sort only, like the API
            // # This should not happen if columns are properly configured,
            // # but just in case, we keep the first sort only.
            sorter = sorter[0];
        }

        // Update the table sort state
        setColumns(getTableColumns(sorter));

        // Call new sorted data
        const [column, direction] = tableToDtoSortOrder(sorter);

        const filterStore = filterStoreRef.current;
        fetch({
            pagination,
            sortColumn: column,
            sortDirection: direction,
            searchTerm: filterStore.searchTerm,
        });
    };

    const debouncedFetch = useSearchDebounce(fetch);
    useEffect(() => {
        const disposer = autorun(() => {
            const filterStore = filterStoreRef.current;
            debouncedFetch({
                pagination: initialPaginationState,
                sortColumn: defaultColumnSort,
                sortDirection: defaultDirectionSort,
                searchTerm: filterStore.searchTerm,
            });
        });
        return (): void => {
            disposer();
        };
    }, [debouncedFetch, defaultColumnSort, defaultDirectionSort]);

    const onRowClick = (foodMerchant: FoodMerchantDto): void => {
        if (foodMerchant && foodMerchant.id && foodMerchant.bbotRestaurant === null) {
            history.push(`/locations/${id}/management/merchants/${foodMerchant.id}`);
        }
        else if (foodMerchant && foodMerchant.id && foodMerchant.bbotRestaurant !== null) {
            history.push(`/locations/${id}/management/merchants/${foodMerchant.id}/details`);
        }
    };

    const onCreationComplete = useCallback(
        (success: boolean) => {
            setCreateModalOpen(false);
            const filterStore = filterStoreRef.current;
            if (success) {
                fetch({
                    pagination: pagination,
                    sortColumn: defaultColumnSort,
                    sortDirection: defaultDirectionSort,
                    searchTerm: filterStore.searchTerm,
                });
            }
        },
        [fetch, pagination, defaultColumnSort, defaultDirectionSort]
    );

    const onReorderComplete = (): void => {
        const filterStore = filterStoreRef.current;
        fetch({
            pagination: pagination,
            sortColumn: defaultColumnSort,
            sortDirection: defaultDirectionSort,
            searchTerm: filterStore.searchTerm,
        });
        setReorderModalOpen(false);
    };

    return (
        <div className="FoodMerchants">
            <LocationHeader
                title={t('Food.food_and_beverages_merchant')}
                subTitle={t('Food.food_and_beverages_merchant_subtitle')}
                defaultImg={<FoodMerchantsIcon fill={theme['primary-color']} />}
                routes={breadcrumbs}
                action={
                    (userStore.isAdmin ||
                        new UserPermissionUtils(userStore).UserCanViewContent([id])) && (
                        <ActionMenu options={actions} type="primary" trigger="click" />
                    )
                }
            />
            <Content>
                <TableFilters filterStore={filterStoreRef.current} includeSearch />
                <Table
                    className="table-striped-rows table-action-rows"
                    bordered
                    columns={columns}
                    rowKey={(record: FoodMerchantDto): string => record.id || ''}
                    dataSource={data}
                    loading={loading}
                    pagination={pagination}
                    onChange={handleTableChange}
                    onRow={(row: FoodMerchantDto) => ({
                        onClick: (): void => {
                            onRowClick(row || null);
                        },
                    })}
                />
            </Content>
            {(userStore.isAdmin || new UserPermissionUtils(userStore).UserCanViewContent([id])) &&
                createModalOpen && (
                    <FoodMerchantModal
                        visible={createModalOpen}
                        onComplete={onCreationComplete}
                        locationId={id}
                    />
                )}
            {(userStore.isAdmin || new UserPermissionUtils(userStore).UserCanViewContent([id])) &&
                reorderModalOpen && (
                    <ReorderFoodMerchantsModal
                        visible={reorderModalOpen}
                        onComplete={onReorderComplete}
                    />
                )}
        </div>
    );
};

export default FoodMerchants;
